// LifeIQ Cyber Fraud & Scam Safety — product data configuration
// Companion imprint to Chinnu Talking Adventures, reusing the same generic
// product/checkout architecture (see /app/frontend/src/data/books.js).
//
// COMMAND 01 (2026-02): titles, page counts, chapter names and topic
// bullets below are derived from the three authoritative EBOOK PDFs
// supplied by the owner (LifeIQ Publications, First Edition 2026).
// Covers = PDF page 1 renders; previews are extracted from the actual
// PDFs at the exact pages specified by the owner. Nothing is invented.

export const CYBER_SAFETY_ACCENT = {
  navy: "#0D1B2A",
  navySurface: "#1B2A4A",
  navyBorder: "#2A3C60",
  gold: "#D4A030",
  goldHover: "#E5B140",
  teal: "#2A7B88",
  red: "#D9383A",
  cream: "#FAFAF7",
  creamSurface: "#F2F0E9",
  textMuted: "#5A6B82",
};

export const CYBER_SAFETY_SERIES = {
  name: "LifeIQ Cyber Fraud & Scam Safety",
  descriptor: "A 3-Book Family Digital Safety Series",
  supporting: "Fraud Awareness · Digital Protection · Recovery",
  reflex: ["PAUSE", "VERIFY", "REPORT"],
  use: "For the whole family.",
  intro:
    "Scams change names, apps, messages and disguises. This three-book family series helps readers recognise the recurring patterns underneath fraud, verify independently before acting, and know what to do quickly if something has already gone wrong.",
};

export const CYBER_SAFETY_SUPPORT = {
  email: "LifeIqedtech@gmail.com",
  whatsapp: "+91 9059112617",
  whatsapp_link: "https://wa.me/919059112617",
};

/**
 * Cyber Safety products. Each entry is fully data-driven — the product
 * page template renders whatever is here, exactly like Chinnu.
 * `order_base` and `accentColor` are read by BuyButton/StickyBuyBar so the
 * SAME checkout engine renders in this series' own visual identity.
 */
export const CYBER_BOOKS = [
  {
    product_id: "CYBER-B01",
    slug: "scam-engine-money-traps",
    book_number: 1,
    title: "The Scam Engine & Money Traps",
    progression_label: "RECOGNISE THE PATTERN",
    label: "BOOK 1 OF 3",
    subtitle: "A Family Guide to Fraud Awareness, Digital Protection & Recovery",
    short_description:
      "The recurring moves underneath many common scams — including UPI/QR payment confusion, fake investment platforms and task-based earning traps — and the three-word reflex that helps interrupt them.",
    topics: [
      "The Family Digital Shield — ten habits that reduce openings before any scam arrives",
      "The Pattern Behind Many Scams — six common moves used in many frauds",
      "The QR Code That Makes You Pay — UPI/QR payment traps",
      "The Investment Trap — fake investment platforms",
      "The Task-Based Trap — task-based earning scams",
      "If Something Goes Wrong — Act Immediately",
      "The 90-Second Future Scam Test",
      "Notes & Family Plan",
    ],
    chapters: [
      { name: "The Family Digital Shield", desc: "Ten habits that protect you before any scam arrives" },
      { name: "Chapter 1: The Pattern Behind Many Scams", desc: "Six common moves used in many frauds" },
      { name: "Chapter 2: The QR Code That Makes You Pay", desc: "UPI / QR payment confusion" },
      { name: "Chapter 3: The Investment Trap", desc: "Fake investment platforms" },
      { name: "Chapter 4: The Task-Based Trap", desc: "Task-based earning scams" },
      { name: "If Something Goes Wrong — Act Immediately", desc: "Emergency response steps" },
      { name: "The 90-Second Future Scam Test", desc: "A quick check for anything new" },
      { name: "Notes & Family Plan", desc: "A family action plan" },
    ],
    pages: 35,
    regular_price: "₹99",
    price: "₹49",
    regular_price_value: 99,
    price_value: 49,
    savings: "₹50",
    offer_label: "Launch Offer",
    language: "English",
    format: "Digital PDF",
    cover_asset: "/api/book-assets/covers/scam-engine-money-traps.jpg",
    preview_assets: [
      "/api/book-assets/previews/scam-engine-money-traps/p1.jpg",
      "/api/book-assets/previews/scam-engine-money-traps/p3.jpg",
      "/api/book-assets/previews/scam-engine-money-traps/p6.jpg",
      "/api/book-assets/previews/scam-engine-money-traps/p8.jpg",
      "/api/book-assets/previews/scam-engine-money-traps/p11.jpg",
      "/api/book-assets/previews/scam-engine-money-traps/p14.jpg",
      "/api/book-assets/previews/scam-engine-money-traps/p22.jpg",
      "/api/book-assets/previews/scam-engine-money-traps/p31.jpg",
    ],
    order_base: "/books/cyber-safety/order",
    download_asset: "/api/books/download/scam-engine-money-traps",
    download_ready: true,
  },
  {
    product_id: "CYBER-B02",
    slug: "borrowed-trust-false-opportunities",
    book_number: 2,
    title: "Borrowed Trust & False Opportunities",
    progression_label: "RECOGNISE THE DISGUISE",
    label: "BOOK 2 OF 3",
    subtitle: "A Family Guide to Fraud Awareness, Digital Protection & Recovery",
    short_description:
      "How fraud borrows authority, affection, identity and opportunity to appear genuine — from digital arrest scares to fake job offers and cloned booking sites.",
    topics: [
      "Digital Arrest — the lawful process vs. the digital-arrest disguise",
      "The App Disguised as an Official Notice — malicious APK / challan files",
      "The SIM Upgrade You Didn't Request — SIM / eSIM takeover",
      "When the Relationship Is the Bait — relationship fraud",
      "The Job Offer That Asks You to Pay — fake job offers",
      "Fake Game Deals & Online Money Traps",
      "The Overseas Job That Becomes a Trap — trafficking risk",
      "When a Photo Is Misused",
      "The Seller That Wasn't Real — cloned booking/seller sites",
      "The Scam Costume Atlas — quick-reference guide",
      "If Something Goes Wrong — Act Immediately",
    ],
    chapters: [
      { name: "Chapter 5: Digital Arrest", desc: "The lawful process vs. the digital-arrest disguise" },
      { name: "Chapter 6: The App Disguised as an Official Notice", desc: "Malicious APK / challan files" },
      { name: "Chapter 7: The SIM Upgrade You Didn't Request", desc: "SIM / eSIM takeover" },
      { name: "Chapter 8: When the Relationship Is the Bait", desc: "Relationship fraud" },
      { name: "Chapter 9: The Job Offer That Asks You to Pay", desc: "Fake job offers" },
      { name: "Chapter 10: Fake Game Deals & Online Money Traps", desc: "In-game and online money traps" },
      { name: "Chapter 11: The Overseas Job That Becomes a Trap", desc: "Overseas job / trafficking risk" },
      { name: "Chapter 12: When a Photo Is Misused", desc: "Photo misuse" },
      { name: "Chapter 13: The Seller That Wasn't Real", desc: "Fake sellers / cloned booking sites" },
      { name: "The Scam Costume Atlas", desc: "Quick-reference guide to fraud disguises" },
      { name: "If Something Goes Wrong — Act Immediately", desc: "Emergency response steps" },
    ],
    pages: 57,
    regular_price: "₹99",
    price: "₹49",
    regular_price_value: 99,
    price_value: 49,
    savings: "₹50",
    offer_label: "Launch Offer",
    language: "English",
    format: "Digital PDF",
    cover_asset: "/api/book-assets/covers/borrowed-trust-false-opportunities.jpg",
    preview_assets: [
      "/api/book-assets/previews/borrowed-trust-false-opportunities/p1.jpg",
      "/api/book-assets/previews/borrowed-trust-false-opportunities/p2.jpg",
      "/api/book-assets/previews/borrowed-trust-false-opportunities/p5.jpg",
      "/api/book-assets/previews/borrowed-trust-false-opportunities/p7.jpg",
      "/api/book-assets/previews/borrowed-trust-false-opportunities/p12.jpg",
      "/api/book-assets/previews/borrowed-trust-false-opportunities/p17.jpg",
      "/api/book-assets/previews/borrowed-trust-false-opportunities/p37.jpg",
      "/api/book-assets/previews/borrowed-trust-false-opportunities/p51.jpg",
    ],
    order_base: "/books/cyber-safety/order",
    download_asset: "/api/books/download/borrowed-trust-false-opportunities",
    download_ready: true,
  },
  {
    product_id: "CYBER-B03",
    slug: "ai-fakes-account-traps-recovery",
    book_number: 3,
    title: "AI Fakes, Account Traps & Recovery",
    progression_label: "KNOW HOW TO RESPOND",
    label: "BOOK 3 OF 3",
    subtitle: "A Family Guide to Fraud Awareness, Digital Protection & Recovery",
    short_description:
      "Modern impersonation risks, account misuse and urgent-message fraud — plus exactly what to do in the first hour if something has already gone wrong.",
    topics: [
      "The Voice That Sounds Real — voice-cloning & impersonation",
      "When a Loan App Becomes Harassment — predatory loan-app risk",
      "When Someone Wants to Use Your Bank Account — mule-account risk",
      "The Urgent Message From Someone You Know — impersonation of familiar identities",
      "The First Hour — what to do right away",
      "Something Already Happened — Where Do I Start?",
      "After Fraud — What Never to Do",
      "Where Do I Report? — official routes and helplines",
      "The LifeIQ Family Protection Card",
    ],
    chapters: [
      { name: "Chapter 14: The Voice That Sounds Real", desc: "Voice-cloning and impersonation" },
      { name: "Chapter 15: When a Loan App Becomes Harassment", desc: "Predatory loan-app risk" },
      { name: "Chapter 16: When Someone Wants to Use Your Bank Account", desc: "Mule-account risk" },
      { name: "Chapter 17: The Urgent Message From Someone You Know", desc: "Urgent requests from familiar identities" },
      { name: "Chapter 18: The First Hour", desc: "What to do right away" },
      { name: "Something Already Happened — Where Do I Start?", desc: "First steps after fraud" },
      { name: "After Fraud — What Never to Do", desc: "Avoiding a second fraud" },
      { name: "Where Do I Report?", desc: "Official reporting routes and helplines" },
      { name: "The LifeIQ Family Protection Card", desc: "A printable family reference card" },
    ],
    pages: 39,
    regular_price: "₹99",
    price: "₹49",
    regular_price_value: 99,
    price_value: 49,
    savings: "₹50",
    offer_label: "Launch Offer",
    language: "English",
    format: "Digital PDF",
    cover_asset: "/api/book-assets/covers/ai-fakes-account-traps-recovery.jpg",
    preview_assets: [
      "/api/book-assets/previews/ai-fakes-account-traps-recovery/p1.jpg",
      "/api/book-assets/previews/ai-fakes-account-traps-recovery/p2.jpg",
      "/api/book-assets/previews/ai-fakes-account-traps-recovery/p5.jpg",
      "/api/book-assets/previews/ai-fakes-account-traps-recovery/p7.jpg",
      "/api/book-assets/previews/ai-fakes-account-traps-recovery/p15.jpg",
      "/api/book-assets/previews/ai-fakes-account-traps-recovery/p22.jpg",
      "/api/book-assets/previews/ai-fakes-account-traps-recovery/p25.jpg",
      "/api/book-assets/previews/ai-fakes-account-traps-recovery/p34.jpg",
    ],
    order_base: "/books/cyber-safety/order",
    download_asset: "/api/books/download/ai-fakes-account-traps-recovery",
    download_ready: true,
  },
];

export const CYBER_BUNDLE = {
  product_id: "CYBER-BUNDLE-03",
  slug: "complete-series",
  title: "Complete Cyber Fraud & Scam Safety Series",
  description:
    "3 digital books, 131 pages, one family reflex — recognise the pattern, recognise the disguise, and know how to respond. Download each title separately or grab the complete ZIP.",
  format: "Digital PDF",
  regular_price: "₹297",
  price: "₹99",
  regular_price_value: 297,
  price_value: 99,
  savings: "₹198",
  regular_price_label: "Regular Combined Price",
  offer_label: "Complete Series Offer",
  order_base: "/books/cyber-safety/order",
  download_asset: "/api/books/download/complete-series",
  download_ready: true,
};

export const CYBER_SAFETY_FAQ = [
  {
    q: "What format will I receive?",
    a: "A high-quality PDF digital edition — yours to keep, delivered instantly after payment on a private download page.",
  },
  {
    q: "How do I download the book?",
    a: "Right after a successful Razorpay payment you'll arrive on a private download page with a one-tap download button. Keep your order reference handy — it lets us help you re-download later.",
  },
  {
    q: "Can I read it on a phone, tablet or computer?",
    a: "Yes. The PDF opens beautifully on any phone, tablet, laptop or e-reader — no special app needed.",
  },
  {
    q: "Do these books guarantee I won't be scammed?",
    a: "No book can promise that, and we don't claim it. These books help you recognise recurring patterns and know how to respond quickly — awareness, not a guarantee.",
  },
  {
    q: "Can I share the PDF?",
    a: "Your purchase is for personal and family use — read it together at home as often as you like, but please don't forward the file outside your family.",
  },
  {
    q: "How do I contact LifeIQ for purchase support?",
    a: "Email LifeIqedtech@gmail.com or WhatsApp +91 9059112617. We reply within one working day.",
  },
];

// Helpers (mirrors data/books.js conventions)
export const findCyberBook = (slug) => CYBER_BOOKS.find((b) => b.slug === slug);

export const findCyberProduct = (slug) =>
  slug === CYBER_BUNDLE.slug ? CYBER_BUNDLE : findCyberBook(slug);

/**
 * GENUINE trust signals only — real, verifiable facts about the books and
 * the store's own mechanics. No star ratings, review counts, "as seen in"
 * logos or quotes belong here. Safe to display as-is, unstyled-as-badges.
 */
export const CYBER_SAFETY_TRUST_FACTS = [
  "LifeIQ Publications",
  "For the whole family",
  "3-book structured series",
  "131 pages total",
  "18 chapters",
  "Pause · Verify · Report",
  "Secure Razorpay checkout",
  "Protected digital download",
];
export const CYBER_SAFETY_INFO_REVIEWED_LABEL = "Information reviewed August 2026";

/**
 * TRUST / REVIEWS — reusable, currently DISABLED. Do not enable or add any
 * entry until the owner supplies genuine, consented reviewer/media content.
 * No placeholder, invented, or "coming soon" items belong in this array —
 * `TrustReviews` renders nothing at all while `CYBER_SAFETY_TRUST_REVIEWS`
 * is empty and/or `CYBER_SAFETY_TRUST_REVIEWS_ENABLED` is false.
 *
 * When the owner approves real content, push objects here matching one of:
 *  { type: "early_reader", quote, name, role?, consent_status: "approved" }
 *  { type: "professional_review", quote, name, designation, organisation, source?, date, consent_status: "approved" }
 *  { type: "school_feedback", quote, organisation, logo?, attribution, consent_status: "approved" }
 *  { type: "media_mention", publication, logo?, url, date, consent_status: "approved" }
 * Every item MUST carry consent_status: "approved" — TrustReviews silently
 * skips any item missing that exact value, as a second safety net.
 */
export const CYBER_SAFETY_TRUST_REVIEWS_ENABLED = false;
export const CYBER_SAFETY_TRUST_REVIEWS = [];
